import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Phone, MessageSquare, Mail, FileText, Calendar } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { requireTenantUser } from '@/integrations/supabase/session';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import type { CommunicationType, CommunicationDirection } from '@/types/communications';

interface LogCommunicationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultCustomerId?: string;
  defaultLeadId?: string;
}

const typeOptions: { value: CommunicationType; label: string; icon: React.ElementType }[] = [
  { value: 'call', label: 'Call', icon: Phone },
  { value: 'sms', label: 'SMS', icon: MessageSquare },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'note', label: 'Note', icon: FileText },
  { value: 'meeting', label: 'Meeting', icon: Calendar },
];

export function LogCommunicationDialog({
  open,
  onOpenChange,
  defaultCustomerId,
  defaultLeadId,
}: LogCommunicationDialogProps) {
  const queryClient = useQueryClient();
  const [type, setType] = useState<CommunicationType>('call');
  const [direction, setDirection] = useState<CommunicationDirection>('outbound');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [outcome, setOutcome] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState(defaultCustomerId || '');

  const resetForm = () => {
    setType('call');
    setDirection('outbound');
    setSubject('');
    setContent('');
    setOutcome('');
    setCustomerSearch('');
    setSelectedCustomerId(defaultCustomerId || '');
  };

  const logCommunication = useMutation({
    mutationFn: async () => {
      const { user, tenantId: companyId } = await requireTenantUser();

      if (!selectedCustomerId) {
        throw new Error('Please select a customer');
      }

      const { error } = await supabase.from('customer_communications').insert({
        company_id: companyId,
        customer_id: selectedCustomerId,
        lead_id: defaultLeadId || null,
        communication_type: type,
        direction,
        subject: subject || null,
        content: content || null,
        outcome: outcome || null,
        created_by: user.id,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['communications'] });
      queryClient.invalidateQueries({ queryKey: ['communications-count'] });
      toast({ title: 'Communication logged successfully' });
      resetForm();
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to log communication',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Log Communication</DialogTitle>
          <DialogDescription>
            Record a customer interaction manually.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Type Selection */}
          <div className="space-y-2">
            <Label>Type</Label>
            <ToggleGroup
              type="single"
              value={type}
              onValueChange={(value) => value && setType(value as CommunicationType)}
              className="justify-start"
            >
              {typeOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <ToggleGroupItem key={option.value} value={option.value} aria-label={option.label}>
                    <Icon className="h-4 w-4 mr-1" />
                    {option.label}
                  </ToggleGroupItem>
                );
              })}
            </ToggleGroup>
          </div>

          {/* Direction */}
          {type !== 'note' && (
            <div className="space-y-2">
              <Label>Direction</Label>
              <Select value={direction} onValueChange={(v) => setDirection(v as CommunicationDirection)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inbound">Inbound (received)</SelectItem>
                  <SelectItem value="outbound">Outbound (sent/made)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Customer Search (simplified for now) */}
          {!defaultCustomerId && (
            <div className="space-y-2">
              <Label>Customer ID</Label>
              <Input
                placeholder="Enter customer ID..."
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Tip: Use the customer detail page to log communications with pre-filled customer
              </p>
            </div>
          )}

          {/* Subject (for emails) */}
          {type === 'email' && (
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                placeholder="Email subject..."
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>
          )}

          {/* Content/Notes */}
          <div className="space-y-2">
            <Label>{type === 'note' ? 'Note' : 'Summary'}</Label>
            <Textarea
              placeholder={
                type === 'call' 
                  ? 'What was discussed...' 
                  : type === 'sms'
                  ? 'Message content...'
                  : type === 'email'
                  ? 'Email content summary...'
                  : 'Notes...'
              }
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={3}
            />
          </div>

          {/* Outcome */}
          {type !== 'note' && (
            <div className="space-y-2">
              <Label>Outcome (optional)</Label>
              <Input
                placeholder="e.g., Appointment scheduled, Voicemail left..."
                value={outcome}
                onChange={(e) => setOutcome(e.target.value)}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => logCommunication.mutate()} 
            disabled={logCommunication.isPending || !selectedCustomerId}
          >
            {logCommunication.isPending ? 'Saving...' : 'Log Communication'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
